import React, { useCallback, useEffect, useState } from 'react';
import { apiErrorMessage } from '../api/client';
import * as pricingApi from '../api/pricingApi';
import { FormulationPricing } from '../api/pricingApi';
import { Card, ConfirmDialog, ErrorBanner, Skeleton } from '../components/ui';
import { formatDateTime, formatINR } from '../utils/format';

/**
 * The price customers pay for a made-to-order formulation.
 *
 * This is the single most consequential number in the admin panel — it is what
 * every new custom bottle is charged at — so the page is deliberately slow to
 * act on: a confirmation dialog spells out the before/after and states what the
 * change does and does not touch before anything is written.
 */
export default function PricingPage() {
  const [pricing, setPricing] = useState<FormulationPricing | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionError, setActionError] = useState('');
  const [saved, setSaved] = useState(false);

  const [price, setPrice] = useState('');
  const [mrp, setMrp] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [saving, setSaving] = useState(false);

  const fill = (p: FormulationPricing) => {
    setPricing(p);
    setPrice(String(p.price));
    setMrp(p.mrp === null ? '' : String(p.mrp));
  };

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      fill(await pricingApi.getFormulationPricing());
    } catch (err) {
      setError(apiErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const nextPrice = Number(price);
  const nextMrp = mrp.trim() === '' ? null : Number(mrp);

  // Same rules the service enforces, checked here so the confirm dialog is only
  // ever reachable with values that will actually be accepted.
  const priceValid = Number.isFinite(nextPrice) && nextPrice > 0;
  const mrpValid = nextMrp === null || (Number.isFinite(nextMrp) && nextMrp > nextPrice);
  const changed =
    !!pricing && (nextPrice !== pricing.price || nextMrp !== pricing.mrp);
  const canSave = priceValid && mrpValid && changed && !saving;

  const handleSave = async () => {
    setSaving(true);
    setActionError('');
    try {
      fill(await pricingApi.updateFormulationPricing(nextPrice, nextMrp));
      setConfirming(false);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      setActionError(apiErrorMessage(err));
      setConfirming(false);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl font-semibold">Pricing</h1>
        <p className="text-sm text-sub">What customers pay for a custom formulation</p>
      </div>

      {error && <ErrorBanner message={error} onRetry={load} />}
      {actionError && <ErrorBanner message={actionError} />}

      {loading ? (
        <Card>
          <Skeleton className="h-40 w-full" />
        </Card>
      ) : (
        <div className="grid gap-6 lg:grid-cols-2">
          <Card title="Custom formulation (50 ml)">
            <div className="space-y-4">
              <div>
                <label className="label">Selling price (₹, GST inclusive)</label>
                <input
                  className="input"
                  type="number"
                  min={1}
                  value={price}
                  onChange={(e) => setPrice(e.target.value)}
                />
                <p className="mt-1 text-xs text-sub">
                  Charged on every custom bottle created from now on. Bags and orders that
                  already exist keep the price they were created at.
                </p>
                {!priceValid && price !== '' && (
                  <p className="mt-1 text-xs text-bad">Enter a positive amount</p>
                )}
              </div>

              <div>
                <label className="label">Compare-at price / MRP (₹)</label>
                <input
                  className="input"
                  type="number"
                  min={1}
                  placeholder="Leave blank — no strikethrough"
                  value={mrp}
                  onChange={(e) => setMrp(e.target.value)}
                />
                <p className="mt-1 text-xs text-sub">
                  Shown struck through on the landing page. Leave this blank unless the
                  product genuinely sold at the higher price — a compare-at price that was
                  never charged is a misleading price claim.
                </p>
                {!mrpValid && (
                  <p className="mt-1 text-xs text-bad">MRP must be higher than the selling price</p>
                )}
              </div>

              <div className="flex items-center gap-3 border-t border-edge pt-4">
                <button className="btn-gold" disabled={!canSave} onClick={() => setConfirming(true)}>
                  Save price
                </button>
                {saved && <span className="text-xs text-green-600 dark:text-green-400">Saved ✓</span>}
                {changed && !saved && (
                  <button className="btn-ghost !text-xs" onClick={() => pricing && fill(pricing)}>
                    Discard changes
                  </button>
                )}
              </div>

              {pricing?.updated_at && (
                <p className="text-xs text-sub">
                  Last changed {formatDateTime(pricing.updated_at)}
                  {pricing.updated_by ? ` by ${pricing.updated_by}` : ''}
                </p>
              )}
            </div>
          </Card>

          {/* What the storefront will render, built from the same rules the
              landing page uses — so nobody has to deploy to find out. */}
          <Card title="Customer sees">
            <div className="space-y-4">
              <div className="border border-edge p-5 text-center">
                <p className="text-xs uppercase tracking-widest text-sub">Launch pricing</p>
                <p className="mt-3 text-2xl font-semibold">
                  {nextMrp !== null && mrpValid && (
                    <span className="mr-3 text-base font-normal text-sub line-through">
                      {formatINR(nextMrp)}
                    </span>
                  )}
                  {priceValid ? formatINR(nextPrice) : '—'}
                </p>
                {nextMrp !== null && mrpValid && priceValid && (
                  <p className="mt-2 text-xs text-sub">
                    Save {formatINR(nextMrp - nextPrice)} today
                  </p>
                )}
              </div>
              <p className="text-xs text-sub">
                {nextMrp === null
                  ? 'With no compare-at price the landing page hides the launch-offer card entirely and shows a single price.'
                  : 'The launch-offer card appears on the landing page with this saving.'}
              </p>
            </div>
          </Card>
        </div>
      )}

      <ConfirmDialog
        open={confirming}
        title="Change the price customers pay?"
        confirmLabel={saving ? 'Saving…' : 'Yes, change the price'}
        busy={saving}
        message={
          <div className="space-y-3">
            <p>
              Selling price{' '}
              <span className="font-semibold text-ink">{formatINR(pricing?.price ?? 0)}</span> →{' '}
              <span className="font-semibold text-ink">{formatINR(nextPrice)}</span>
            </p>
            <p>
              Compare-at price{' '}
              <span className="font-semibold text-ink">
                {pricing?.mrp === null || pricing?.mrp === undefined ? 'none' : formatINR(pricing.mrp)}
              </span>{' '}
              → <span className="font-semibold text-ink">{nextMrp === null ? 'none' : formatINR(nextMrp)}</span>
            </p>
            <p>
              This takes effect immediately on the storefront and applies to every custom
              bottle created from now on. Carts and orders that already exist are not
              repriced.
            </p>
          </div>
        }
        onConfirm={handleSave}
        onClose={() => setConfirming(false)}
      />
    </div>
  );
}
